<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { onMount, tick } from 'svelte';
  import type { UploadedDocument } from '../api';
  import { api } from '../api';
  import { householdsStore } from '../stores/householdsStore';
  import { activeHouseholdStore } from '../stores/activeHouseholdStore';
  import { uploadSessionStore } from '../stores/uploadSessionStore';
  import { categoriesStore } from '../stores/categoriesStore';
  import { followupsStore } from '../stores/followupsStore';
  import { toastStore } from '../stores/toastStore';
  import { userSettings } from '../stores/settingsStore';
  import { formatDate } from '../settings';
  import { deadlineSuggestionErrorMessage } from '../followups';
  import FileText from 'lucide-svelte/icons/file-text';
  import Plus from 'lucide-svelte/icons/plus';
  import X from 'lucide-svelte/icons/x';
  import Tag from 'lucide-svelte/icons/tag';
  import Users from 'lucide-svelte/icons/users';
  import Home from 'lucide-svelte/icons/home';
  import Layers from 'lucide-svelte/icons/layers';
  import StickyNote from 'lucide-svelte/icons/sticky-note';
  import Pencil from 'lucide-svelte/icons/pencil';
  import Check from 'lucide-svelte/icons/check';
  import AlertTriangle from 'lucide-svelte/icons/alert-triangle';
  import CalendarClock from 'lucide-svelte/icons/calendar-clock';
  import { Badge, ConfidenceBadge, ExtractedDataEditor, DateInput, StatusBadge } from './ui/index';
  import { isFinishedStatus } from '../documentStatus';
  import { memberRoleLabelKey } from '../memberRoles';
  import {
    WARNING_FLAG_I18N_KEYS,
    recognizedWarningFlags,
    distinctSentenceFlags,
  } from '../warningFlags';

  export let document: UploadedDocument | null = null;

  // Spec 087 (#486) E15/E16: the "Frist" row only exists when the parent explicitly opts in
  // (Inbox.svelte, after its own nachladen) — Upload.svelte passes only `document`, unchanged,
  // so this stays false there and the row never renders (S34).
  export let showDeadlineRow = false;
  export let deadlineLoading = false;
  // Review F12: a failed nachladen GET is a distinct "not loaded" state — never silently shown as
  // "none recognized", which would look like a confident negative rather than an unknown.
  export let deadlineLoadError = false;
  export let deadlineSuggestion: { date: string; subject: string; confidence: number } | null =
    null;
  export let deadlineActiveFollowup: { id: string; dueDate: string; subject: string } | null = null;
  // Review F1: once the accept/dismiss decision is through server-side (including the F1(c) 409
  // bypass), the card tells the parent so it can clear `deadlineSuggestion` from its props — that
  // prop change is what stops a second "Als geprüft markieren" from resubmitting the decision.
  export let onDeadlineDecided: (() => void) | undefined = undefined;

  let deadlineAccepted = false;
  let deadlineEditDate = '';
  let deadlineEditSubject = '';
  let initializedForDeadlineKey: string | null = null;
  // Review F1(c): a dezenter, non-blocking hint — set when a 409 deadline_suggestion.not_open
  // let confirming through anyway (someone else already decided the suggestion, e.g. another tab).
  let deadlineHint: string | null = null;
  // Review F6: the error region is scrolled into view + focused so a failure below the fold isn't
  // silently invisible under the sticky action bar (QS-16) — simpler than duplicating the message
  // near the "Als geprüft markieren" button, which lives in a different component (Inbox.svelte).
  let errorRegionEl: HTMLDivElement | undefined;

  // Re-initialize the accept checkbox + editable fields whenever a genuinely new suggestion
  // arrives (new document selected, or the same document's suggestion changes) — never on every
  // render, so the user's in-progress edits/checkbox choice survive unrelated reactivity.
  $: {
    const key = deadlineSuggestion
      ? `${document?.id}:${deadlineSuggestion.date}:${deadlineSuggestion.subject}`
      : (document?.id ?? null);
    if (key !== initializedForDeadlineKey) {
      initializedForDeadlineKey = key;
      deadlineAccepted = false;
      deadlineEditDate = deadlineSuggestion?.date ?? '';
      deadlineEditSubject = deadlineSuggestion?.subject ?? '';
      deadlineHint = null;
    }
  }

  // Localize a stored member role ("Parent"/"Child"/"Other") for display (#241),
  // falling back to the raw value for unknown/custom roles.
  function roleLabel(role: string): string {
    const key = memberRoleLabelKey(role);
    return key ? $_(key) : role;
  }

  let selectedPersonIds: string[] = [];
  let selectedHouseholdId: string = '';
  let userTags: string[] = [];
  let notes: string = '';
  let saving = false;
  let saveError: string | null = null;
  let newTag: string = '';
  let lastDocumentId: string | null = null;
  // Spec 084 E6/E10 (review F2: shared with DocumentDetail.svelte via lib/warningFlags.ts) —
  // the review card is another place the same warning must appear.
  $: warningFlags = recognizedWarningFlags(document?.extractedData?.['system:warningFlags']);

  // Editable copy of the document's extracted values (#256). In the Inbox detail
  // panel everything is always-editable and persisted on "Confirm & Finish" via
  // saveIfDirty — extracted values follow the same model (no separate save button).
  let editableExtractedValues: Record<string, string> = {};
  // When false (default), extracted data is displayed read-only. User must click
  // "Correct" to enter edit mode. Resets on document change to avoid stale edit state.
  let editingExtracted = false;

  // Load households on mount
  onMount(() => {
    if (!$householdsStore || $householdsStore.length === 0) {
      householdsStore.load();
    }
  });

  // Initialize form from document's saved data when a different document is selected
  $: if (document && document.id !== lastDocumentId) {
    lastDocumentId = document.id;
    selectedHouseholdId = document.householdId || $activeHouseholdStore;
    selectedPersonIds = [...(document.assignedTo || [])];
    userTags = [...(document.userTags || [])];
    notes = document.notes || '';
    editableExtractedValues = { ...(document.extractedData || {}) };
    saveError = null;
    editingExtracted = false;
  }

  // Get current household members for person selection
  $: currentHousehold = $householdsStore.find((h) => h.id === selectedHouseholdId);
  $: members = currentHousehold?.members || [];

  // Dirty check: has the user changed anything from the saved values?
  $: isDirty = document
    ? JSON.stringify([...selectedPersonIds].sort()) !==
        JSON.stringify([...(document.assignedTo || [])].sort()) ||
      JSON.stringify([...userTags].sort()) !==
        JSON.stringify([...(document.userTags || [])].sort()) ||
      selectedHouseholdId !== (document.householdId || '') ||
      notes !== (document.notes || '') ||
      !extractedValuesEqual(editableExtractedValues, document.extractedData || {})
    : false;

  // Order-insensitive comparison of two flat string maps.
  function extractedValuesEqual(a: Record<string, string>, b: Record<string, string>): boolean {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const k of keys) {
      if ((a[k] ?? '') !== (b[k] ?? '')) return false;
    }
    return true;
  }

  async function handleSave() {
    if (!document) return;

    saving = true;
    saveError = null;
    uploadSessionStore.setSaving(true);

    try {
      // Single atomic API call — avoids Firestore race conditions from parallel writes
      await api.documents.updateDocumentMetadata(document.id, {
        assignedTo: selectedPersonIds,
        userTags: userTags,
        householdId: selectedHouseholdId,
        notes: notes || '',
        extractedValues: editableExtractedValues,
      });

      // Update store so switching away and back shows saved data
      uploadSessionStore.updateDocument(document.id, {
        assignedTo: [...selectedPersonIds],
        userTags: [...userTags],
        householdId: selectedHouseholdId,
        notes: notes || undefined,
        extractedData: { ...editableExtractedValues },
      });

      saveError = null;
    } catch (err) {
      saveError = err instanceof Error ? err.message : $_('upload.detail.save_error');
      focusErrorRegion();
    } finally {
      saving = false;
      uploadSessionStore.setSaving(false);
    }
  }

  // Review F6: scrolls the error region into view and moves focus to it — the simpler of the two
  // options the review offered, and self-contained (no cross-component wiring into Inbox.svelte's
  // sticky action bar).
  async function focusErrorRegion() {
    await tick(); // the error region only mounts once `saveError` becomes truthy
    // jsdom (component tests) doesn't implement scrollIntoView — guard rather than skip the test.
    errorRegionEl?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    errorRegionEl?.focus();
  }

  // Review F1(blocker): "Als geprüft markieren" resolves the deadline-suggestion decision (accept
  // with the edited values, or dismiss) ONLY AFTER metadata is safely saved — a half-success
  // (accept goes through, then the metadata save or reviewDocument fails) used to leave a
  // Sackgasse where the suggestion was already gone but the document stuck in the Inbox. Order is
  // now: handleSave → accept/dismiss → toast/refresh only on full success. Bulk-Freigabe never
  // calls this, so an open suggestion untouched by bulk stays open (S32).
  async function resolveDeadlineSuggestionIfAny(): Promise<boolean> {
    if (!showDeadlineRow || !document || !deadlineSuggestion) return true;

    // U1 (review): the checkbox is set but the date field is empty (DateInput clears it on an
    // unparsable blur) — never accept a followup without a date. Keep the document in the
    // Inbox, surface the error, do not dismiss either (the user's checkbox choice is unresolved).
    if (deadlineAccepted && !deadlineEditDate) {
      saveError = $_('upload.detail.deadline_date_required');
      focusErrorRegion();
      return false;
    }

    deadlineHint = null;
    try {
      if (deadlineAccepted) {
        await api.documents.acceptDeadlineSuggestion(document.id, {
          dueDate: deadlineEditDate,
          subject: deadlineEditSubject,
        });
        toastStore.success($_('upload.detail.deadline_accept_success'));
        followupsStore.refresh();
      } else {
        await api.documents.dismissDeadlineSuggestion(document.id);
      }
      onDeadlineDecided?.();
      return true;
    } catch (err) {
      const errorCode = (err as { errorCode?: string } | null)?.errorCode;
      if (errorCode === 'deadline_suggestion.not_open') {
        // F1(c): someone else already decided this suggestion (e.g. another tab) — confirming
        // must not be blocked by that; just say so, quietly, and let the parent clear the prop.
        deadlineHint = $_('upload.detail.deadline_hint_already_decided');
        onDeadlineDecided?.();
        return true;
      }
      saveError = deadlineSuggestionErrorMessage(err, $_);
      focusErrorRegion();
      return false;
    }
  }

  // Exposed for the parent (Inbox): "Confirm & Finish" persists pending edits before
  // marking the document reviewed. Returns false if the save failed (#209).
  export async function saveIfDirty(): Promise<boolean> {
    saveError = null;

    if (isDirty) {
      await handleSave();
      if (saveError !== null) return false;
    }

    return resolveDeadlineSuggestionIfAny();
  }

  function handleAddTag() {
    if (!newTag.trim()) return;
    if (userTags.includes(newTag.trim())) return;

    userTags = [...userTags, newTag.trim()];
    newTag = '';
  }

  function handleRemoveTag(tag: string) {
    userTags = userTags.filter((t) => t !== tag);
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddTag();
    }
  }

  function togglePerson(personId: string) {
    if (selectedPersonIds.includes(personId)) {
      selectedPersonIds = selectedPersonIds.filter((id) => id !== personId);
    } else {
      selectedPersonIds = [...selectedPersonIds, personId];
    }
  }
</script>

{#if !document}
  <div class="flex flex-col items-center justify-center py-16 px-4 text-center">
    <FileText class="w-10 h-10 text-text-muted mb-3" strokeWidth={1.5} />
    <p class="text-sm font-medium text-text-secondary mb-1">{$_('upload.detail.no_selection')}</p>
    <p class="text-xs text-text-muted">{$_('upload.detail.select_hint')}</p>
  </div>
{:else}
  <div class="p-4">
    <!-- Header -->
    <div class="mb-4 pb-3 border-b border-border">
      <h3 class="text-sm font-semibold text-text break-words">{document.fileName}</h3>
      {#if !isFinishedStatus(document)}
        <div class="mt-2"><StatusBadge status={document.status} /></div>
      {/if}
    </div>

    <div class="flex flex-col gap-4">
      <!-- Deadline suggestion (Spec 087, #486 E18, review U7) — same hint stack as the AI
           failure warning below, directly under the header. Matches the duplicate-warning
           banner's visual pattern (Inbox.svelte): icon left, title, description, rounded
           border — info tone (not accent/warning, review U4) so it stays visually distinct
           from both the AI warning and the duplicate banner. -->
      {#if showDeadlineRow}
        {#if deadlineLoadError}
          <div
            class="flex items-start gap-3 p-3 rounded-lg bg-bg-alt border border-border text-sm"
            data-testid="upload-detail-deadline-load-error"
          >
            <CalendarClock
              class="w-4 h-4 text-text-muted shrink-0 mt-0.5"
              strokeWidth={2}
              aria-hidden="true"
            />
            <p class="text-text-secondary">{$_('upload.detail.deadline_load_error')}</p>
          </div>
        {:else if deadlineActiveFollowup}
          <div
            class="flex items-start gap-3 p-3 rounded-lg bg-bg-alt border border-border text-sm"
            data-testid="upload-detail-deadline-box"
          >
            <CalendarClock
              class="w-4 h-4 text-text-muted shrink-0 mt-0.5"
              strokeWidth={2}
              aria-hidden="true"
            />
            <p class="text-text-secondary" data-testid="upload-detail-deadline-followup-value">
              {$_('upload.detail.deadline_followup_label')}
              {formatDate(deadlineActiveFollowup.dueDate, $userSettings)}
            </p>
          </div>
        {:else if deadlineSuggestion && !deadlineLoading}
          <div
            class="flex items-start gap-3 p-3 rounded-lg bg-info/10 border border-info/30 text-sm"
            data-testid="upload-detail-deadline-box"
          >
            <CalendarClock
              class="w-4 h-4 text-info shrink-0 mt-0.5"
              strokeWidth={2}
              aria-hidden="true"
            />
            <div class="flex-1 min-w-0">
              <p class="font-medium text-text" data-testid="upload-detail-deadline-title">
                {$_('upload.detail.deadline_title')}
                {formatDate(deadlineSuggestion.date, $userSettings)}
              </p>
              <p class="text-text-secondary mt-0.5 break-words">{deadlineSuggestion.subject}</p>
              <label class="flex items-center gap-1.5 mt-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  bind:checked={deadlineAccepted}
                  class="accent-accent"
                  data-testid="upload-detail-deadline-accept-checkbox"
                />
                <span class="text-text">{$_('upload.detail.deadline_accept_checkbox')}</span>
              </label>
              {#if deadlineHint}
                <p class="text-xs text-text-muted mt-1" data-testid="upload-detail-deadline-hint">
                  {deadlineHint}
                </p>
              {/if}
              {#if deadlineAccepted}
                <div class="flex flex-col sm:flex-row gap-2 mt-1.5">
                  <DateInput
                    bind:value={deadlineEditDate}
                    class="flex-1 min-w-0"
                    aria-label={$_('upload.detail.deadline_date_label')}
                  />
                  <input
                    type="text"
                    bind:value={deadlineEditSubject}
                    placeholder={$_('upload.detail.deadline_subject_placeholder')}
                    aria-label={$_('upload.detail.deadline_subject_label')}
                    maxlength="200"
                    class="flex-[2] px-2.5 py-1.5 bg-bg-alt border border-border rounded text-sm text-text focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                  />
                </div>
              {/if}
            </div>
          </div>
        {/if}
      {/if}

      <!-- AI failure warning (Spec 084 E6/E10) — placed above Classification, the review
           card's counterpart to DocumentDetail.svelte's banner. -->
      {#if warningFlags.length > 0}
        <div
          class="p-3 bg-accent/10 border border-accent/20 rounded-lg flex items-start gap-3"
          data-testid="upload-detail-ai-warning-banner"
        >
          <AlertTriangle class="w-4 h-4 text-accent shrink-0 mt-0.5" strokeWidth={1.5} />
          <div class="flex flex-col gap-1">
            {#each distinctSentenceFlags(warningFlags, $_) as flag (flag)}
              <p
                class="text-sm font-medium text-text"
                data-testid={`upload-detail-ai-warning-reason-${flag}`}
              >
                {$_(WARNING_FLAG_I18N_KEYS[flag])}
              </p>
            {/each}
          </div>
        </div>
      {/if}

      <!-- Classification -->
      {#if document.classification}
        <section>
          <h4
            class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-text-muted mb-2"
          >
            <Layers class="w-3.5 h-3.5" strokeWidth={1.5} />
            {$_('upload.detail.classification')}
          </h4>
          <div class="flex flex-col gap-1.5">
            <div class="flex justify-between items-center px-2.5 py-1.5 bg-bg-alt rounded">
              <span class="text-xs text-text-muted">{$_('upload.detail.category')}</span>
              <span class="text-sm font-medium text-text">
                {categoriesStore.getLocalizedCategoryName(document.classification.category)}
              </span>
            </div>
            <div class="flex justify-between items-center px-2.5 py-1.5 bg-bg-alt rounded">
              <span class="text-xs text-text-muted">{$_('upload.detail.subcategory')}</span>
              <span class="text-sm font-medium text-text">
                {categoriesStore.getLocalizedSubCategoryName(
                  document.classification.category,
                  document.classification.subCategory,
                )}
              </span>
            </div>
            <div class="flex justify-between items-center px-2.5 py-1.5 bg-bg-alt rounded">
              <span class="text-xs text-text-muted">{$_('upload.detail.confidence')}</span>
              <ConfidenceBadge
                score={document.classification.confidence}
                isManualClassified={document.classification.isManualClassified ?? false}
              />
            </div>
          </div>
        </section>
      {/if}

      <!-- Extracted Data — editable in the review flow (#256). Corrections are
           persisted together with the other metadata on "Confirm & Finish".
           ExtractedDataEditor dispatches a `change` event with a freshly
           assigned Record — this triggers the $: isDirty reactive statement
           and avoids the in-place-mutation bug (bind:value on object property
           does not trigger Svelte reactivity). -->
      {#if Object.keys(editableExtractedValues).length > 0}
        <section>
          <div class="flex items-center justify-between mb-2">
            <h4
              class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-text-muted"
            >
              <FileText class="w-3.5 h-3.5" strokeWidth={1.5} />
              {$_('upload.detail.extracted_data')}
            </h4>
            <button
              type="button"
              on:click={() => {
                editingExtracted = !editingExtracted;
              }}
              class="flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-text-muted hover:text-accent hover:bg-bg-alt rounded transition-colors"
              title={editingExtracted
                ? $_('upload.detail.extracted_done')
                : $_('upload.detail.extracted_edit')}
            >
              {#if editingExtracted}
                <Check class="w-3 h-3" strokeWidth={2} />
                {$_('upload.detail.extracted_done')}
              {:else}
                <Pencil class="w-3 h-3" strokeWidth={2} />
                {$_('upload.detail.extracted_edit')}
              {/if}
            </button>
          </div>
          <ExtractedDataEditor
            values={editableExtractedValues}
            readonly={!editingExtracted}
            on:change={(e) => {
              editableExtractedValues = e.detail;
            }}
          />
        </section>
      {/if}

      <!-- Household Selection -->
      <section>
        <h4
          class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-text-muted mb-2"
        >
          <Home class="w-3.5 h-3.5" strokeWidth={1.5} />
          {$_('upload.detail.household')}
        </h4>
        {#if $householdsStore.length > 0}
          <select
            bind:value={selectedHouseholdId}
            class="w-full px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
          >
            {#each $householdsStore as household (household.id)}
              <option value={household.id}>
                {household.name || household.id} ({household.type})
              </option>
            {/each}
          </select>
        {:else}
          <p class="text-sm text-text-muted text-center py-3 bg-bg-alt rounded">
            {$_('upload.detail.no_households')}
          </p>
        {/if}
      </section>

      <!-- Person Assignment -->
      <section>
        <h4
          class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-text-muted mb-2"
        >
          <Users class="w-3.5 h-3.5" strokeWidth={1.5} />
          {$_('upload.detail.persons')}
        </h4>
        {#if members.length > 0}
          <div class="flex flex-col gap-1">
            {#each members as member (member.id)}
              <label
                class="flex items-center gap-2.5 cursor-pointer px-2.5 py-2 rounded transition-colors hover:bg-surface-hover"
              >
                <input
                  type="checkbox"
                  checked={selectedPersonIds.includes(member.id)}
                  on:change={() => togglePerson(member.id)}
                  class="accent-accent"
                />
                <span class="text-sm text-text">
                  {member.name}
                  {#if member.role}
                    <span class="text-text-muted text-xs ml-1">({roleLabel(member.role)})</span>
                  {/if}
                </span>
              </label>
            {/each}
          </div>
        {:else}
          <p class="text-sm text-text-muted text-center py-3 bg-bg-alt rounded">
            {$_('upload.detail.no_members')}
          </p>
        {/if}
      </section>

      <!-- User Tags -->
      <section>
        <h4
          class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-text-muted mb-2"
        >
          <Tag class="w-3.5 h-3.5" strokeWidth={1.5} />
          {$_('upload.detail.tags')}
        </h4>
        <div class="flex gap-2">
          <input
            type="text"
            bind:value={newTag}
            on:keydown={handleKeyDown}
            placeholder={$_('upload.detail.tag_placeholder')}
            class="flex-1 px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
          />
          <button
            on:click={handleAddTag}
            type="button"
            aria-label={$_('components.document_detail.add_tag_aria')}
            title={$_('components.document_detail.add_tag_aria')}
            class="inline-flex items-center justify-center min-w-8 min-h-8 px-2 text-text-muted hover:text-accent hover:bg-bg-alt rounded transition-colors"
          >
            <Plus class="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        {#if userTags.length > 0}
          <div class="flex flex-wrap gap-1.5 mt-2">
            {#each userTags as tag (tag)}
              <Badge variant="accent" removable on:remove={() => handleRemoveTag(tag)}>
                {tag}
              </Badge>
            {/each}
          </div>
        {/if}
      </section>

      <!-- Notes -->
      <section>
        <h4
          class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-text-muted mb-2"
        >
          <StickyNote class="w-3.5 h-3.5" strokeWidth={1.5} />
          {$_('upload.detail.notes')}
        </h4>
        <textarea
          bind:value={notes}
          placeholder={$_('upload.detail.notes_placeholder')}
          rows="3"
          class="w-full px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 resize-y"
        ></textarea>
      </section>
    </div>

    <!-- Save errors surface here; saving is triggered by "Confirm & Finish" (#209). Review F6:
         scrolled into view + focused (focusErrorRegion) so a failure below the fold isn't silently
         invisible under the sticky action bar. -->
    {#if saveError}
      <div class="mt-4 pt-4 border-t border-border">
        <div
          bind:this={errorRegionEl}
          tabindex="-1"
          class="px-3 py-2 bg-error/10 border border-error/30 rounded text-sm text-error focus:outline-none"
          data-testid="upload-detail-save-error"
        >
          {saveError}
        </div>
      </div>
    {/if}
  </div>
{/if}
